import axios from 'axios';
import pMap from 'p-map-browser';

import getLogger from '../../../../util/logger';
import { PHOTO_BATCH_PHOTOS_UPLOAD_STATUS } from '../../../../constants/photo-batch';
import { photoUploadSuccess } from '../../../../actions/photo-upload';
import sleep from '../../../../util/sleep';
import { isAlbumFolder } from '../../../../util/album-folder';
import { isAlbum } from '../../../../util/album';
import { photoUploadFilter } from '../../../../util/photo-upload';
import { isVideo } from '../../components/UploadProgressTracking/helpers';

const log = getLogger('FileUploadActions');

/**
 * How often to perform a retry.
 */
const DEFAULT_RETRY_DELAY_SECONDS = 2;

/**
 * How many times to retry a file before giving it up.
 */
const MAX_RETRIES = 5;

/**
 * Number of files to upload concurrently.
 */
const CONCURRENCY = 3;

/**
 * Number of bytes per part (currently set to 5 GiB), if we need to split a video
 *
 */
export const MULTIPART_CHUNK_SIZE = 1024 ** 3 * 5;

export const UPLOAD_RESET = 'upload/UPLOAD_RESET';
export const UPLOAD_START = 'upload/UPLOAD_START';
export const UPLOAD_COMPLETE = 'upload/UPLOAD_COMPLETE';
export const UPLOAD_FAIL = 'upload/UPLOAD_FAIL';
export const UPLOAD_CANCEL_REQUEST = 'upload/UPLOAD_CANCEL_REQUEST';
export const UPLOAD_ITEM_PROGRESS = 'upload/UPLOAD_ITEM_PROGRESS';
export const UPLOAD_ITEM_PART_PROGRESS = 'upload/UPLOAD_ITEM_PART_PROGRESS';
export const UPLOAD_ITEM_COMPLETE = 'upload/UPLOAD_ITEM_COMPLETED';
export const UPLOAD_ITEM_FAIL = 'upload/UPLOAD_ITEM_FAIL';

export const uploadReset = () => {
  return { type: UPLOAD_RESET };
};

const uploadStart = (files, cancelToken) => {
  return { type: UPLOAD_START, files: files.filter(Boolean), cancelToken };
};

const uploadComplete = () => {
  return { type: UPLOAD_COMPLETE };
};

const uploadFail = () => {
  return { type: UPLOAD_FAIL };
};

const uploadItemProgress = uploadItem => {
  return { type: UPLOAD_ITEM_PROGRESS, uploadItem };
};

const uploadItemPartProgress = uploadPartItem => {
  return { type: UPLOAD_ITEM_PART_PROGRESS, uploadPartItem };
};

const uploadItemComplete = uploadItem => {
  return { type: UPLOAD_ITEM_COMPLETE, uploadItem };
};

const uploadItemFail = uploadItem => {
  return { type: UPLOAD_ITEM_FAIL, uploadItem };
};

export const uploadCancel = () => {
  return { type: UPLOAD_CANCEL_REQUEST };
};

/**
 * Performs the actual file upload.
 * This is the piece that actually "tracks" the upload itself.
 *
 * @param {Object} opts Options object.
 * @param {Object} opts.file The File object to upload.
 * @param {String} opts.uploadUrl Where to upload the file.
 * @param {Function} opts.dispatch Redux dispatch.
 * @param {CancelToken} opts.cancelToken
 * @param {String} opts.defaultContentType A default content type to use in case of missing one.
 * @returns {Object<PhotoBatchPhotoRecord>} A photoBatchPhotoRecord record object.
 */
const fileUpload = async ({
  file,
  dispatch,
  uploadUrl,
  cancelToken,
  defaultContentType = 'application/octet-stream',
}) => {
  const uploadStartDate = new Date().toISOString();

  log.info(' fileUpload :: Uploading file: ', file, ' to URL: ', uploadUrl);

  dispatch(
    uploadItemProgress({
      file,
      startDate: uploadStartDate,
      progressEvent: new ProgressEvent('progress', { total: file.size }),
    }),
  );

  // eslint-disable-next-line no-useless-catch
  try {
    await axios.put(uploadUrl, file, {
      headers: {
        'Content-Type': file.type || defaultContentType,
      },
      onUploadProgress: progressEvent => {
        dispatch(
          uploadItemProgress({
            file,
            progressEvent,
            startDate: uploadStartDate,
          }),
        );
      },
      cancelToken: cancelToken?.token,
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Performs a multipart file upload.
 * This is the piece that actually "tracks" the upload itself.
 *
 * @param {Object} opts Options object.
 * @param {Object} opts.file The File object to upload.
 * @param {String} opts.multipartId The multipart id (utilized to abort or complete)
 * @param {Array} opts.multipartParts The part numbers & upload urls
 * @param {Function} opts.dispatch Redux dispatch.
 * @param {CancelToken} opts.cancelToken
 * @param {String} opts.defaultContentType A default content type to use in case of missing one.
 * @returns {Object<PhotoBatchPhotoRecord>} A photoBatchPhotoRecord record object.
 */
const multipartFileUpload = async ({
  file,
  multipartId,
  multipartParts,
  dispatch,
  cancelToken,
  defaultContentType = 'application/octet-stream',
}) => {
  let isAborted = false;
  const multipartAbortController = new AbortController();
  const uploadStartDate = new Date().toISOString();

  log.info(
    ' fileUpload :: Uploading file: ',
    file,
    ' - id: ',
    multipartId,
    ' to multiple urls: ',
    multipartParts,
  );

  const fileParts = multipartParts.map(
    ({ part_number: id, upload_url: uploadUrl }) => ({
      id,
      uploadUrl,
      file: file.slice(
        (id - 1) * MULTIPART_CHUNK_SIZE,
        id * MULTIPART_CHUNK_SIZE,
      ),
    }),
  );

  dispatch(
    uploadItemProgress({
      file,
      startDate: uploadStartDate,
      progressEvent: new ProgressEvent('progress', { total: file.size }),
      partsProgressEvents: fileParts.map(part => ({
        id: part.id,
        filename: file.name,
        progressEvent: new ProgressEvent('progress', { total: part.file.size }),
      })),
    }),
  );

  const completedParts = await pMap(
    fileParts,
    async part => {
      // eslint-disable-next-line no-useless-catch
      try {
        const { headers } = await axios.put(part.uploadUrl, part.file, {
          headers: {
            'Content-Type': file.type || defaultContentType,
          },
          onUploadProgress: progressEvent => {
            if (!isAborted) {
              dispatch(
                uploadItemPartProgress({
                  id: part.id,
                  filename: file.name,
                  progressEvent,
                }),
              );
            }
          },
          cancelToken: cancelToken?.token,
        });

        return {
          part_number: part.id,
          etag: headers.etag.replaceAll('"', ''),
        };
      } catch (error) {
        multipartAbortController.abort();
        isAborted = true;
        throw error;
      }
    },
    { signal: multipartAbortController.signal },
  );

  return { completedParts };
};

/**
 * Handles the process of creating a PhotoBatchPhoto record and ensuring it gets uploaded. Does not handle the actual
 * upload, but does handle retries.
 *
 * @param {Object} opts List of Options.
 * @param {Object} opts.photo The File object to upload.
 * @param {Function} opts.photoBatchPhotosUploadMutation Mutation to create a photo batch photo.
 * @param {Function} opts.photoBatchPhotosUpdateMutation Mutation to update a photo batch photo during the upload.
 * @param {Function} opts.dispatch Redux dispatch.
 * @param {Object} opts.photoBatch An already available & created PhotoBatchRecord.
 * @param {CancelToken} opts.cancelToken
 * @param {number} opts.retry
 */
const photoUploadProcess = async ({
  photo,
  photoBatchPhotosUploadMutation,
  photoBatchPhotosUpdateMutation,
  photoBatch,
  dispatch,
  cancelToken,
  retry = 1,
}) => {
  try {
    log.info('photoUploadProcess() :: Upload is about to start...: ', photo);
    const photoBatchPhotosUploadRes = await photoBatchPhotosUploadMutation({
      photoBatchId: photoBatch.id,
      photos: [
        {
          file_name: photo.name,
          content_type: photo.type,
          file_size: photo.size,
        },
      ],
    });

    const [
      photoBatchPhotoRecord,
    ] = photoBatchPhotosUploadRes.data.photoBatchPhotosUpload.photos;

    const photoBatchPhotoId = photoBatchPhotoRecord.photo_batch_photo.id;
    const uploadStartDate = new Date().toISOString();
    // Update with success
    try {
      await fileUpload({
        file: photo,
        uploadUrl: photoBatchPhotoRecord.upload_url,
        photoBatchPhotoRecord,
        photoBatchPhotosUpdateMutation,
        cancelToken,
        dispatch,
        // Cannot be application/octet-stream for HEIC files.
        defaultContentType: '',
      });

      await photoBatchPhotosUpdateMutation({
        photoBatchPhotoId,
        uploadStatus: PHOTO_BATCH_PHOTOS_UPLOAD_STATUS.COMPLETED,
        uploadStartDate,
        uploadEndDate: new Date().toISOString(),
      });
    } catch (uploadError) {
      await photoBatchPhotosUpdateMutation({
        photoBatchPhotoId,
        uploadStatus: PHOTO_BATCH_PHOTOS_UPLOAD_STATUS.FAILED,
        uploadStartDate,
        uploadEndDate: new Date().toISOString(),
      });

      throw uploadError;
    }

    const photoId = photoBatchPhotoRecord.photo_batch_photo.photo.id;

    dispatch(
      photoUploadSuccess(
        {
          id: photoId,
          file: photo,
        },
        true,
      ),
    );

    log.info('photoUploadProcess() :: Photo uploaded: ', photoId);

    return { photoId };
  } catch (error) {
    if (
      retry < MAX_RETRIES &&
      (error.networkError || error.message === 'Network Error')
    ) {
      log.info(
        `photoUploadProcess() :: Network Error. Waiting ${DEFAULT_RETRY_DELAY_SECONDS}s to retry photo: `,
        photo,
      );

      await sleep(DEFAULT_RETRY_DELAY_SECONDS * 1000);

      return photoUploadProcess({
        photo,
        photoBatchPhotosUploadMutation,
        photoBatchPhotosUpdateMutation,
        photoBatch,
        retry: retry + 1,
        cancelToken,
        dispatch,
      });
    }

    // If a photo fails for any other reason than network errors, just go ahead and give it up.
    log.error('photoUploadProcess() :: Error uploading photo', error);
    return { error };
  }
};

/**
 * Upload photos to waldo servers. Uses the PhotoBatch API so that we can properly
 * track what happens to the photos (if they fail, if they succeed and whatnot), and makes sure
 * to return all uploaded photos IDs & errors separately.
 *
 * @param {Object} opts List of Options.
 * @param {Array<Object>} opts.photos List of photos to upload.
 * @param {Function} opts.photoBatchPhotosUploadMutation Mutation to create a photo batch photo.
 * @param {Function} opts.photoBatchPhotosUpdateMutation Mutation to update a photo batch photo during the upload.
 * @param {Function} opts.photoBatchCreateMutation Mutation to create a Photo batch record.
 * @param {Function} [opts.onPhotoUploadProcessComplete] Callback called every time a file completes upload. Main purpose is for live-tracking the upload progress. Return value is attached to the actual upload item as processCompleteResult.
 * @param {Function} [opts.onPhotoBatchCreated] Callback called when the photo batch gets created. useful if the uploader must keep track of the photoBatchId.
 * @param {Function} opts.dispatch Redux dispatch.
 * @param {Function} opts.getState Redux dispatch.
 *
 */
export const photosUpload = async ({
  photos,
  photoBatchPhotosUploadMutation,
  photoBatchPhotosUpdateMutation,
  photoBatchCreateMutation,
  onPhotoUploadProcessComplete,
  onPhotoBatchCreated,
  dispatch,
  getState,
}) => {
  try {
    const { CancelToken } = axios;
    const cancelToken = CancelToken.source();

    dispatch(uploadStart(photos, cancelToken));

    const {
      data: {
        photoBatchCreate: { photo_batch: photoBatch },
      },
    } = await photoBatchCreateMutation(photos.length);

    onPhotoBatchCreated?.(photoBatch);

    const uploadedPhotos = await pMap(
      photos,
      async photo => {
        const { canceled } = getState().pro.fileUpload;

        if (canceled) {
          return {
            photoId: undefined,
            file: photo,
            error: new Error('Upload canceled by the user'),
          };
        }

        const { error, photoId } = await photoUploadProcess({
          photo,
          photoBatchPhotosUploadMutation,
          photoBatchPhotosUpdateMutation,
          dispatch,
          cancelToken,
          photoBatch,
        });

        const photoItem = {
          photoId,
          file: photo,
          error,
        };

        const processCompleteResult = await onPhotoUploadProcessComplete?.(
          photoItem,
        );

        if (photoItem.photoId) {
          dispatch(
            uploadItemComplete({
              ...photoItem,
              success: true,
              processCompleteResult,
            }),
          );
          // If it's a cancel, we won't report it as failed.
        } else if (!axios.isCancel(error)) {
          dispatch(
            uploadItemFail({
              ...photoItem,
              processCompleteResult,
            }),
          );
        }

        return {
          ...photoItem,
          processCompleteResult,
        };
      },
      { concurrency: CONCURRENCY },
    );

    dispatch(uploadComplete());

    return uploadedPhotos;
  } catch (error) {
    // assume is network error & try to restart.
    // Per-photo errors, as well as assignment & other type of errors are already accounted for.
    // So getting here must be network-related for the most part.
    log.error('photosUpload() :: Error starting upload...', error);

    if (
      error?.message.includes('GraphQL error') ||
      error?.includes('GraphQL error')
    ) {
      // This is a GQL error. Likely a server error and/or an authentication one. Retrying will do no good.
      dispatch(uploadFail());
      return [];
    }

    return photosUpload({
      photos,
      photoBatchPhotosUploadMutation,
      photoBatchPhotosUpdateMutation,
      photoBatchCreateMutation,
      onPhotoUploadProcessComplete,
      dispatch,
      getState,
    });
  }
};

/**
 * @typedef VideoItem Defines a video object containing the file & base properties such as the name, description, etc.
 * @property {Object} file
 * @property {String} name
 * @property {String} description
 * @property {Bool} downloadEnabled
 */

/**
 * Handles the process of creating a PhotoBatchPhoto record and ensuring it gets uploaded. Does not handle the actual
 * upload, but does handle retries.
 *
 * @param {Object} opts List of Options.
 * @param {VideoItem} opts.videoItem The video item object, containing the file and base information.
 * @param {Function} opts.videosUploadMutation Mutation to upload a video.
 * @param {Function} opts.videoUpdateMutation Mutation to update a video.
 * @param {Function} opts.videoDeleteMutation Mutation to delete a video.
 * @param {Function} opts.multipartUploadCompleteMutation Mutation to complete a multipart upload
 * @param {Function} opts.multipartUploadAbortMutation Mutation to abort a multipart upload
 * @param {Function} opts.dispatch Redux dispatch.
 * @param {Function} opts.cancelToken Axios cancel token.
 * @param {number} opts.retry
 * @param {Array} opts.targets
 */
const videoUploadProcess = async ({
  videoItem,
  videosUploadMutation,
  videoUpdateMutation,
  videoDeleteMutation,
  multipartUploadCompleteMutation,
  multipartUploadAbortMutation,
  dispatch,
  retry = 1,
  cancelToken,
  targets = [],
}) => {
  let videoId;
  let multipartUploadId;

  try {
    log.info(
      'videoUploadProcess() :: Upload is about to start...: ',
      videoItem,
    );

    const targetAlbumFoldersIds =
      targets.filter(target => isAlbumFolder(target)).map(({ id }) => id) || [];
    const targetAlbumsIds =
      targets
        .filter(
          target =>
            isAlbum(target) &&
            (!target.photoGroup?.id || target.photoGroup?.id === target.id),
        )
        .map(({ id }) => id) || [];
    const targetPhotoGroupsIds =
      targets
        .filter(
          target =>
            isAlbum(target) &&
            target.photoGroup?.id &&
            target.photoGroup?.id !== target.id,
        )
        .map(({ photoGroup }) => photoGroup.id) || [];

    const partCount = Math.ceil(
      (videoItem.file?.size ?? 0) / MULTIPART_CHUNK_SIZE,
    );

    if (
      partCount > 1 &&
      (!multipartUploadCompleteMutation || !multipartUploadAbortMutation)
    ) {
      log.info(
        `videoUploadProcess() :: Missing mutations to properly process ${videoItem.file.name}}...: `,
        'CompleteMutation is:',
        multipartUploadCompleteMutation,
        'AbortMutation is:',
        multipartUploadAbortMutation,
      );
      throw Error(`Cannot process video file: ${videoItem.file.name}`);
    }

    const {
      data: { videosUpload },
    } = await videosUploadMutation({
      videos: [
        {
          filename: videoItem.file.name,
          content_type: videoItem.file.type || 'application/octet-stream',
          name: videoItem.name,
          description: videoItem.description,
          download_enabled: videoItem.downloadEnabled,
          type: videoItem.type,
          // Always skip, we will always run update afterward regardless.
          video_links_skip_create: true,
          video_links: {
            album_folder_ids: targetAlbumFoldersIds,
            album_ids: targetAlbumsIds,
            album_photo_group_ids: targetPhotoGroupsIds,
          },
          multipart_upload_parts_count: partCount > 1 ? partCount : undefined,
        },
      ],
    });

    const [videoUploadRecord] = videosUpload.videos;
    const {
      video: videoRecord,
      upload_url: uploadUrl,
      multipart_upload: multipartUpload,
    } = videoUploadRecord;
    // Store videoId so it can be passed in the errors for cancellation purposes.
    videoId = videoRecord.id;

    if (multipartUpload) {
      // Store multipartUploadId so it can be passed in the errors for cancellation purposes.
      multipartUploadId = multipartUpload.id;

      const { completedParts } = await multipartFileUpload({
        file: videoItem.file,
        multipartId: multipartUpload.id,
        multipartParts: multipartUpload.parts,
        dispatch,
        cancelToken,
      });

      await multipartUploadCompleteMutation({
        fileId: videoRecord.id,
        multipartUploadId,
        parts: completedParts,
      });
    } else {
      await fileUpload({
        file: videoItem.file,
        uploadUrl,
        dispatch,
        cancelToken,
      });
    }

    // Now, update on
    await log.info(
      'videoUploadProcess() :: video has been uploaded: ',
      videoRecord.id,
    );

    return { videoId, multipartUploadId };
  } catch (error) {
    if (
      retry < MAX_RETRIES &&
      (error.networkError || error.message === 'Network Error')
    ) {
      log.info(
        `videoUploadProcess() :: Network Error. Waiting ${DEFAULT_RETRY_DELAY_SECONDS}s to retry video: `,
        videoItem,
      );

      await sleep(DEFAULT_RETRY_DELAY_SECONDS * 1000);

      if (videoId && videoDeleteMutation) {
        await videoDeleteMutation({ videoId });
      }

      // Ensures that hopefully the connection has been re-established before aborting.
      if (videoId && multipartUploadId) {
        await multipartUploadAbortMutation({
          fileId: videoId,
          multipartUploadId,
        });
      }

      return videoUploadProcess({
        videoItem,
        videosUploadMutation,
        videoUpdateMutation,
        multipartUploadCompleteMutation,
        multipartUploadAbortMutation,
        dispatch,
        retry: retry + 1,
        cancelToken,
        targets,
      });
    }

    // If a photo fails for any other reason than network errors, just go ahead and give it up.
    log.error(
      'videoUploadProcess() :: Error uploading video item',
      error,
      videoItem,
    );

    return { videoId, multipartUploadId, error };
  }
};

/**
 * Upload videos to waldo servers.
 *
 * @param {Object} opts List of Options.
 * @param {VideoItem} opts.videoItem The video item to upload.
 * @param {Function} opts.videosUploadMutation Mutation to upload a video file to waldo.
 * @param {Function} opts.videoUpdateMutation Mutation to update a video record.
 * @param {Function} opts.videoDeleteMutation Mutation to delete a video record.
 * @param {Function} opts.multipartUploadCompleteMutation Mutation to abort a multipart record.
 * @param {Function} opts.multipartUploadAbortMutation Mutation to abort a multipart record.
 * @param {Function} [opts.onVideoUploadProcessComplete] Callback called every time a file completes upload. Main purpose is for live-tracking the upload progress. Return value is attached to the actual upload item as processCompleteResult.
 * @param {Function} opts.dispatch Redux dispatch.
 * @param {Array} opts.targets
 */
export const videoUpload = async ({
  videoItem,
  targets,
  videosUploadMutation,
  videoUpdateMutation,
  videoDeleteMutation,
  multipartUploadCompleteMutation,
  multipartUploadAbortMutation,
  onVideoUploadProcessComplete,
  dispatch,
}) => {
  try {
    const { CancelToken } = axios;
    const cancelToken = CancelToken.source();

    dispatch(uploadStart([videoItem.file], cancelToken));

    const { error, videoId, multipartUploadId } = await videoUploadProcess({
      videoItem,
      videosUploadMutation,
      videoUpdateMutation,
      multipartUploadCompleteMutation,
      multipartUploadAbortMutation,
      dispatch,
      cancelToken,
      targets,
    });

    const videoItemUploadResult = {
      ...videoItem,
      videoId,
      error,
    };

    if (axios.isCancel(error)) {
      if (videoId && videoDeleteMutation) {
        videoDeleteMutation({ videoId });
      }

      if (multipartUploadId && multipartUploadAbortMutation) {
        await multipartUploadAbortMutation({
          fileId: videoId,
          multipartUploadId,
        });
      }

      return {
        error,
      }; // Do not do anything else, just cancel.
    }

    const processCompleteResult = await onVideoUploadProcessComplete?.(
      videoItemUploadResult,
    );

    if (videoItemUploadResult.videoId) {
      dispatch(
        uploadItemComplete({
          ...videoItemUploadResult,
          success: true,
          processCompleteResult,
        }),
      );
    } else {
      dispatch(
        uploadItemFail({ ...videoItemUploadResult, processCompleteResult }),
      );
    }

    dispatch(uploadComplete());

    return {
      ...videoItemUploadResult,
      processCompleteResult,
    };
  } catch (error) {
    // assume is network error & try to restart.
    // Per video errors are already accounted for.
    // So getting here must be network-related for the most part.
    log.error('videoUpload() :: Error starting upload...', error);

    return videoUpload({
      videoItem,
      targets,
      videosUploadMutation,
      videoUpdateMutation,
      videoDeleteMutation,
      multipartUploadCompleteMutation,
      multipartUploadAbortMutation,
      onVideoUploadProcessComplete,
      dispatch,
    });
  }
};

/**
 * Upload videos to waldo servers.
 *
 * @param {Object} opts List of Options.
 * @param {Array<File>} opts.files list of files (Photos  & videos) to upload. Must be plain files.
 * @param {Function} opts.videosUploadMutation Mutation to upload a video file to waldo.
 * @param {Function} opts.videoUpdateMutation Mutation to update a video file to waldo.
 * @param {Function} opts.videoDeleteMutation Mutation to delete a video file to waldo.
 * @param {Function} opts.multipartUploadCompleteMutation Mutation to abort a multipart record.
 * @param {Function} opts.multipartUploadAbortMutation Mutation to abort a multipart record.
 * @param {Function} [opts.onVideoUploadProcessComplete] Callback called every time a file completes upload. Main purpose is for live-tracking the upload progress. Return value is attached to the actual upload item as processCompleteResult.
 * @param {Function} opts.photoBatchPhotosUploadMutation Mutation to create a photo batch photo.
 * @param {Function} opts.photoBatchPhotosUpdateMutation Mutation to update a photo batch photo during the upload.
 * @param {Function} opts.photoBatchCreateMutation Mutation to create a Photo batch record.
 * @param {Function} [opts.onPhotoBatchCreated] Callback called when the photo batch gets created. useful if the uploader must keep track of the photoBatchId.
 * @param {Function} [opts.onPhotoUploadProcessComplete] Callback called every time a file completes upload. Main purpose is for live-tracking the upload progress. Return value is attached to the actual upload item as processCompleteResult.
 * @param {Function} opts.dispatch Redux dispatch.
 * @param {Function} opts.getState Redux dispatch.
 */
export const photosAndVideosUpload = async ({
  files,
  videosUploadMutation,
  videoUpdateMutation,
  videoDeleteMutation,
  multipartUploadCompleteMutation,
  multipartUploadAbortMutation,
  onVideoUploadProcessComplete,
  photoBatchPhotosUploadMutation,
  photoBatchPhotosUpdateMutation,
  photoBatchCreateMutation,
  onPhotoBatchCreated,
  onPhotoUploadProcessComplete,
  dispatch,
  getState,
}) => {
  const photos = files.filter(photoUploadFilter);
  const videos = files.filter(file => isVideo(file));
  const { targetAlbums } = getState().albumFolderPhotos;

  try {
    const { CancelToken } = axios;
    const cancelToken = CancelToken.source();

    dispatch(uploadStart(files, cancelToken));

    const {
      data: {
        photoBatchCreate: { photo_batch: photoBatch },
      },
    } = await photoBatchCreateMutation(photos.length);

    onPhotoBatchCreated?.(photoBatch);

    const uploadedPhotos = await pMap(
      photos,
      async photo => {
        const { canceled } = getState().pro.fileUpload;

        if (canceled) {
          return {
            photoId: undefined,
            file: photo,
            error: new Error('Upload canceled by the user'),
          };
        }

        const { error, photoId } = await photoUploadProcess({
          photo,
          photoBatchPhotosUploadMutation,
          photoBatchPhotosUpdateMutation,
          dispatch,
          cancelToken,
          photoBatch,
        });

        const photoItem = {
          photoId,
          file: photo,
          error,
        };

        const processCompleteResult = await onPhotoUploadProcessComplete?.(
          photoItem,
        );

        if (photoItem.photoId) {
          dispatch(
            uploadItemComplete({
              ...photoItem,
              success: true,
              processCompleteResult,
            }),
          );
          // If it's a cancel, we won't report it as failed.
        } else if (!axios.isCancel(error)) {
          dispatch(
            uploadItemFail({
              ...photoItem,
              processCompleteResult,
            }),
          );
        }

        return {
          ...photoItem,
          processCompleteResult,
        };
      },
      { concurrency: CONCURRENCY },
    );

    const targetAlbumsMap = (targetAlbums ?? []).map(target => ({
      ...(target?.album ?? {}),
      photoGroup: target?.album_photo_group,
    }));
    const uploadedVideos = await pMap(videos, async videoFile => {
      const { canceled } = getState().pro.fileUpload;

      if (canceled) {
        return {
          photoId: undefined,
          file: videoFile,
          error: new Error('Upload canceled by the user'),
        };
      }

      const videoItem = {
        file: videoFile,
        name: videoFile.name,
      };

      const { error, videoId, multipartUploadId } = await videoUploadProcess({
        videoItem,
        videosUploadMutation,
        videoUpdateMutation,
        videoDeleteMutation,
        multipartUploadCompleteMutation,
        multipartUploadAbortMutation,
        dispatch,
        cancelToken,
        targets: [...(targetAlbumsMap ?? [])],
      });

      const videoItemUploadResult = {
        ...videoItem,
        videoId,
        error,
      };

      if (axios.isCancel(error)) {
        if (videoId && videoDeleteMutation) {
          await videoDeleteMutation({ videoId });
        }

        if (multipartUploadId && multipartUploadAbortMutation) {
          await multipartUploadAbortMutation({
            fileId: videoId,
            multipartUploadId,
          });
        }

        return {
          error,
        }; // Do not do anything else, just cancel.
      }

      const processCompleteResult = await onVideoUploadProcessComplete?.(
        videoItemUploadResult,
      );

      if (videoItemUploadResult.videoId) {
        dispatch(
          uploadItemComplete({
            ...videoItemUploadResult,
            success: true,
            processCompleteResult,
          }),
        );
      } else {
        dispatch(
          uploadItemFail({ ...videoItemUploadResult, processCompleteResult }),
        );
      }

      return {
        ...videoItemUploadResult,
        processCompleteResult,
      };
    });

    dispatch(uploadComplete());

    return [...(uploadedPhotos || []), ...(uploadedVideos || [])];
  } catch (error) {
    // assume is network error & try to restart.
    // Per-photo errors, as well as assignment & other type of errors are already accounted for.
    // So getting here must be network-related for the most part.
    log.error('photosAndVideosUpload() :: Error starting upload...', error);

    if (
      error?.message.includes('GraphQL error') ||
      error?.includes('GraphQL error')
    ) {
      // This is a GQL error. Likely a server error and/or an authentication one. Retrying will do no good.
      dispatch(uploadFail());
      return [];
    }

    return photosUpload({
      files,
      videosUploadMutation,
      photoBatchPhotosUploadMutation,
      photoBatchPhotosUpdateMutation,
      photoBatchCreateMutation,
      onPhotoBatchCreated,
      onPhotoUploadProcessComplete,
      onVideoUploadProcessComplete,
      dispatch,
      getState,
    });
  }
};
